import { useState } from "react";
import { Form, Input, Button, Select, Spin } from "antd";
import { useLoadScript } from "@react-google-maps/api";
import AddressAutocomplete from "./AddressAutoComplete";
import { useNavigate } from "react-router-dom"; // Import useNavigate

interface FormValues {
  name: string;
  email: string;
  phone: string;
  address: string;
  timeline: string;
}

const libraries: ("places")[] = ["places"];
const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string;

function ContactForm() {
  const navigate = useNavigate(); // Initialize useNavigate
  const [form] = Form.useForm(); // Create a form reference
  const [address, setAddress] = useState("");
  const [isLoading, setIsLoading] = useState(false); // Track loading state
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries,
  });

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, ""); // Remove non-numeric characters
    if (value.length <= 3) {
      value = `(${value}`;
    } else if (value.length <= 6) {
      value = `(${value.slice(0, 3)}) ${value.slice(3)}`;
    } else {
      value = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6, 10)}`;
    }

    form.setFieldsValue({ phone: value });
  };

  const handlePlaceSelect = (place: google.maps.places.PlaceResult) => {
    if (place.formatted_address) {
      setAddress(place.formatted_address);
      form.setFieldsValue({ address: place.formatted_address }); // Update form state
    }
  };

  const onSubmit = async (values: FormValues) => {
    setIsLoading(true); // Start loading

    const contactData = {
      name: values.name,
      email: values.email,
      phone: values.phone,
      address: values.address || address,
      timeline: values.timeline,
    };

    try {
      const response = await fetch('/.netlify/functions/create-contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactData),
      });

      const result = await response.json();
      if (response.ok) {
        console.log('Contact created successfully:', result);
        // Use URLSearchParams to construct query parameters
        const queryParams = new URLSearchParams({
          name: contactData.name,
          phone: contactData.phone,
          email: contactData.email,
          address: contactData.address,
          timeline: contactData.timeline,
        });
        navigate(`/questions?${queryParams.toString()}`);
      } else {
        console.error('Error creating contact:', result);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsLoading(false); // Stop loading after submission is done
    }
  };

  if (!isLoaded) return <div>Loading...</div>;

  return (
    <div className="p-8">
      <Form
        layout="vertical"
        onFinish={onSubmit}
        form={form} // Bind the form reference
        className="bg-white p-4 rounded-lg shadow-lg"
      >
        <h1 className="text-2xl font-bold mb-4 text-center">Get Your Free Cash Offer</h1>
        <Form.Item
          name="name"
          rules={[{ required: true, message: "Please input your name!" }]}
        >
          <Input placeholder="Name" />
        </Form.Item>

        <Form.Item
          name="email"
          rules={[
            { required: true, message: "Please input your email!" },
            { type: "email", message: "Please enter a valid email!" },
          ]}
        >
          <Input placeholder="Email" />
        </Form.Item>

        <Form.Item
          name="phone"
          rules={[
            { required: true, message: "Please input your phone number!" },
            { pattern: /^\(\d{3}\) \d{3}-\d{4}$/, message: "Please enter a valid phone number!" },
          ]}
        >
          <Input
            maxLength={14} // Limit the max length to match the formatted length
            placeholder="Phone"
            value={form.getFieldValue('phone')}
            onChange={handlePhoneChange}
          />
        </Form.Item>

        <Form.Item
          name="address"
          rules={[{ required: true, message: "Please input your address!" }]}
        >
          <AddressAutocomplete
            onSelect={handlePlaceSelect}
            address={address}
            setAddress={setAddress}
            className="ant-input"
          />
        </Form.Item>

        <Form.Item
          name="timeline"
          rules={[{ required: true, message: "Please select a closing time!" }]}
        >
          <Select placeholder="How soon are you looking to sell?">
            <Select.Option value="30 days or less">30 days or less</Select.Option>
            <Select.Option value="1-3 months">1-3 months</Select.Option>
            <Select.Option value="3-6 months">3-6 months</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" block>
            <Spin spinning={isLoading} tip="Submitting...">
              Submit
            </Spin>
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
}

export default ContactForm;
